import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface AmbientSource {
  src: string;
  /** e.g. 'video/mp4; codecs="hvc1"' — the browser skips types it can't play */
  type?: string;
  /** Only use this source when the media query matches */
  media?: string;
}

interface AmbientVideoProps {
  /** One URL, or an ordered list of sources (first playable wins; none = poster only) */
  src: string | AmbientSource[];
  poster: string;
  /** Lighter poster for screens under 768px */
  posterSmall?: string;
  className?: string;
  /** Start loading immediately (above-the-fold), instead of when near the viewport */
  eager?: boolean;
}

/**
 * Decorative, muted, looping background film.
 * - Loads its source when it comes near the viewport; `eager` (above-the-fold)
 *   films wait for the page's load event + an idle moment instead
 * - Poster only on data saver / 2G connections
 * - Plays only while visible and the tab is in the foreground
 * - Shows just the poster frame for prefers-reduced-motion
 */
export function AmbientVideo({ src, poster, posterSmall, className, eager = false }: AmbientVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [load, setLoad] = useState(false);
  // Visitors who asked to save data (or are on a very slow connection) get the poster only
  const [lite] = useState(() => {
    if (typeof navigator === "undefined") return false;
    const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    return !!c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType ?? ""));
  });
  const [reduced, setReduced] = useState(false);
  const [small] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const posterUrl = small && posterSmall ? posterSmall : poster;

  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Above-the-fold films start loading only once the page itself has loaded
  // and the browser is idle, so the poster (not the video) is what paints first
  useEffect(() => {
    if (!eager || lite || reduced) return;
    let idle = 0;
    let timer = 0;
    const start = () => {
      const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) idle = ric(() => setLoad(true), { timeout: 1500 });
      else timer = window.setTimeout(() => setLoad(true), 300);
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      const cic = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idle && cic) cic(idle);
      if (timer) window.clearTimeout(timer);
    };
  }, [eager, lite, reduced]);

  // A <source> added after mount is not picked up until load() is called
  useEffect(() => {
    if (load) ref.current?.load();
  }, [load]);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced || !("IntersectionObserver" in window)) return;
    let visible = false;
    const sync = () => {
      if (visible && !document.hidden) {
        el.play().catch(() => {
          /* autoplay can be refused (e.g. data saver); the poster stays */
        });
      } else {
        el.pause();
      }
    };
    const obs = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !eager && !lite) setLoad(true);
        sync();
      },
      { rootMargin: "300px 0px" },
    );
    obs.observe(el);
    document.addEventListener("visibilitychange", sync);
    return () => {
      obs.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [reduced, load, eager, lite]);

  if (reduced || lite) {
    return <img src={posterUrl} alt="" aria-hidden="true" className={cn("object-cover", className)} decoding="async" />;
  }

  return (
    <video
      ref={ref}
      className={cn("object-cover", className)}
      poster={posterUrl}
      muted
      loop
      playsInline
      preload={load ? "auto" : "none"}
      tabIndex={-1}
      aria-hidden="true"
      disablePictureInPicture
      disableRemotePlayback
    >
      {load &&
        (typeof src === "string" ? [{ src, type: "video/mp4" }] : src).map((s) => (
          <source key={s.src + (s.media ?? "")} src={s.src} type={s.type ?? "video/mp4"} media={s.media} />
        ))}
    </video>
  );
}
